import React from 'react'
import PropTypes from 'prop-types'

import { NAV_MODULE_ICON } from '../../constants'

import DeviceCategoryLink from './DeviceCategoryLink'
import messages from './messages'
import { getVersionIcon } from '../../helpers/utils'

/**
 * Arranges all seven `DeviceCategoryLink` list items in the order they should appear
 * based on supplied params as predicates.
 *
 * @param {{
 *   currentDevice: Device,
 *   query: object,
 *   onSvgLoad: (string) => HTMLOrSVGImageElement,
 *   modulesAvailable: Array<ModulesAvailable>,
 *   showHomeLink?: boolean,
 *   showExpressLink?: boolean,
 *   showMoreDropdown?: boolean,
 *   toggleMoreDropdown?: () => (value: React.SetStateAction<boolean>) => void
 * }}
 * @return {React.ReacctNode}
 */
const DeviceContentCategories = ({
  currentDevice,
  query,
  language,
  onSvgLoad,
  modulesAvailable,
  showHomeLink = false,
  showExpressLink = false,
  showMoreDropdown = false,
  toggleMoreDropdown = () => {},
  version
}) => {
  const translated = messages[language]

  return (
    <>
      {showHomeLink && (
        <li>
          <DeviceCategoryLink
            homeLink
            id="nav-home"
            ariaLabel={translated.home}
            pathname="/"
            query={query}
            iconSrc={getVersionIcon(NAV_MODULE_ICON.home, version)}
            onSvgLoad={onSvgLoad}
            svgKey="home"
            title={translated.home}
            linkClassName="nav-link"
          />
        </li>
      )}

      {showExpressLink && (
        <li>
          <DeviceCategoryLink
            id="nav-expresshelp-link"
            ariaLabel={translated.expresshelp}
            pathname="/expresshelp"
            query={query}
            iconSrc={getVersionIcon(NAV_MODULE_ICON.faq, version)}
            onSvgLoad={onSvgLoad}
            svgKey="faqs"
            title={translated.expresshelp}
            linkClassName="nav-link"
            onClick={() => showMoreDropdown && toggleMoreDropdown()}
          />
        </li>
      )}

      {currentDevice && (
        <li>
          <DeviceCategoryLink
            id="nav-started"
            ariaLabel={translated.overview}
            pathname="/devicehome"
            query={query}
            iconSrc={getVersionIcon(NAV_MODULE_ICON.getStarted, version)}
            onSvgLoad={onSvgLoad}
            svgKey="started"
            title={translated.overview}
            linkClassName="nav-link"
            onClick={() => showMoreDropdown && toggleMoreDropdown()}
          />
        </li>
      )}

      {currentDevice && modulesAvailable.tutorials && (
        <li>
          <DeviceCategoryLink
            id="nav-tutorials"
            ariaLabel={translated.tutorials}
            pathname="/tutorials"
            query={query}
            iconSrc={getVersionIcon(NAV_MODULE_ICON.tutorial, version)}
            onSvgLoad={onSvgLoad}
            svgKey="tutorials"
            title={translated.tutorials}
            linkClassName="nav-link"
            onClick={() => showMoreDropdown && toggleMoreDropdown()}
          />
        </li>
      )}

      {currentDevice && modulesAvailable.faqs && (
        <li>
          <DeviceCategoryLink
            id="nav-faqs"
            ariaLabel={translated.faqs}
            pathname="/faqs"
            query={query}
            iconSrc={getVersionIcon(NAV_MODULE_ICON.faq, version)}
            onSvgLoad={onSvgLoad}
            svgKey="faqs"
            title={translated.faqs}
            linkClassName="nav-link"
            onClick={() => showMoreDropdown && toggleMoreDropdown()}
          />
        </li>
      )}

      {currentDevice && modulesAvailable.guides && (
        <li>
          <DeviceCategoryLink
            id="nav-guides"
            ariaLabel={translated.guides}
            pathname="/guides"
            query={query}
            iconSrc={getVersionIcon(NAV_MODULE_ICON.guide, version)}
            onSvgLoad={onSvgLoad}
            svgKey="guides"
            title={translated.guides}
            linkClassName="nav-link"
            onClick={() => showMoreDropdown && toggleMoreDropdown()}
          />
        </li>
      )}
      {currentDevice && modulesAvailable.videos && (
        <li>
          <DeviceCategoryLink
            id="nav-videos"
            ariaLabel={translated.videos}
            pathname="/videos"
            query={query}
            iconSrc={getVersionIcon(NAV_MODULE_ICON.video, version)}
            onSvgLoad={onSvgLoad}
            svgKey="videos"
            title={translated.videos}
            linkClassName="nav-link"
            onClick={() => showMoreDropdown && toggleMoreDropdown()}
          />
        </li>
      )}
    </>
  )
}

DeviceContentCategories.propTypes = {
  currentDevice: PropTypes.shape({
    customerID: PropTypes.string,
    id: PropTypes.number.isRequired,
    image_location: PropTypes.string.isRequired,
    manufacturer: PropTypes.string,
    name: PropTypes.string,
    name_pretty: PropTypes.string.isRequired,
    os: PropTypes.string,
    slug: PropTypes.string.isRequired,
    vanity_name: PropTypes.string
  }),
  query: PropTypes.object.isRequired,
  language: PropTypes.string.isRequired,
  onSvgLoad: PropTypes.func.isRequired,
  modulesAvailable: PropTypes.shape({
    tutorials: PropTypes.bool,
    faqs: PropTypes.bool,
    guides: PropTypes.bool,
    videos: PropTypes.bool
  }).isRequired,
  showHomeLink: PropTypes.bool,
  showExpressLink: PropTypes.bool,
  toggleMoreDropdown: PropTypes.func,
  showMoreDropdown: PropTypes.bool,
  version: PropTypes.string
}

export default DeviceContentCategories



// WEBPACK FOOTER //
// ./src/components/SubHeader/DeviceContentCategories.js